import { SpecialtyBadges } from '../components/SpecialtyBadges';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import ShareButton from '../components/ShareButton';
import { SEO, buildLocalBusinessSchema } from '../components/SEO';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import { Star, MapPin, Clock, Building2, Home, Car, ShieldCheck, ClipboardList, MessageSquare } from 'lucide-react';
import { ProviderBadge } from '../components/ProviderBadge';
import { ProviderServices } from '../components/provider-profile/ProviderServices';
import { ProviderPortfolio } from '../components/provider-profile/ProviderPortfolio';
import { ProviderReviewsList } from '../components/provider-profile/ProviderReviewsList';
import { formatAddress } from '../utils/format';
import { getPolicy } from '../utils/policies';

const CATEGORY_LABELS = {
  hair: 'Hair',
  nails: 'Nails',
  lashes: 'Lashes & Brows',
  makeup: 'Make-up & Glam'
};

const SERVICE_TYPE_META = {
  salon: { label: 'Salon / Studio', icon: Building2, color: 'bg-stone-100 text-stone-700' },
  home: { label: 'Home-based', icon: Home, color: 'bg-stone-100 text-stone-700' },
  mobile: { label: 'Mobile', icon: Car, color: 'bg-amber-100 text-amber-700' }
};

export default function ProviderProfile() {
  const { providerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noticeHours, setNoticeHours] = useState(0);
  const [reviews, setReviews] = useState([]);

  const resolvedId = providerId || location.pathname.replace(/^\/@/, '');

  useEffect(() => {
    fetchProvider();
    // fetchProvider closes over resolvedId so re-creating it each render is
    // safe; adding it to deps would loop. resolvedId dep is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId]);

  const fetchProvider = async () => {
    try {
      const response = await api.get(`/providers/u/${resolvedId}`).catch(() => null);
      if (response?.data) {
        setProvider(response.data);
      } else {
        const fallback = await api.get(`/providers/${resolvedId}`);
        setProvider(fallback.data);
      }
    } catch (error) {
      console.error('Error fetching provider:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (provider?.provider_id) {
      api.get(`/providers/${provider.provider_id}/calendar`)
        .then(res => setNoticeHours(res.data.min_notice_hours || 0))
        .catch(() => {});
      api.get(`/reviews/provider/${provider.provider_id}`)
        .then(res => setReviews(res.data || []))
        .catch(() => {});
    }
  }, [provider?.provider_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
          <div className="animate-pulse">
            <div className="h-48 bg-stone-200 rounded-xl mb-4"></div>
            <div className="h-8 bg-stone-200 rounded w-1/3 mb-2"></div>
            <div className="h-6 bg-stone-200 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 text-center">
          <h1 className="font-heading text-3xl font-medium text-[#1A1A1A] mb-4">Provider Not Found</h1>
          <p className="text-stone-500 mb-6">This profile doesn't exist or hasn't been approved yet.</p>
          <Link to="/discover">
            <Button className="btn-primary">Browse Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = provider.username ? `/@${provider.username}` : `/provider/${provider.provider_id}`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title={provider.brand_name}
        description={provider.bio || `${provider.brand_name} offers beauty services in ${provider.location || 'the UK'}. Book online with Zuriya.`}
        image={provider.profile_image}
        url={`${window.location.origin}/@${provider.username}`}
        type="profile"
        jsonLd={buildLocalBusinessSchema(provider)}
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <Breadcrumbs items={[
          { label: 'Discover', href: '/discover' },
          { label: provider.brand_name },
        ]} />

        {provider.is_tutorial && (
          <div
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
            data-testid="tutorial-banner"
          >
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-amber-200 text-amber-900 text-xs font-bold flex-shrink-0">i</span>
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Tutorial profile.</span> This is
              a demo account used for product walk-throughs. Bookings are
              disabled and no payments will be taken.
            </div>
          </div>
        )}

        {/* Provider Header Card */}
        <div className="card-surface p-8 mb-6" data-testid="provider-header">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#D4AF37] flex items-center justify-center text-white text-3xl md:text-4xl font-heading flex-shrink-0 overflow-hidden">
              {provider.profile_image ? (
                <img src={provider.profile_image} alt={provider.brand_name} className="w-full h-full object-cover" />
              ) : (
                provider.brand_name?.charAt(0)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A]" data-testid="provider-name">
                      {provider.brand_name}
                    </h1>
                    <ProviderBadge badge={provider.badge} size="sm" />
                    {provider.top_rated && <ProviderBadge badge="top_rated" size="sm" />}
                    {provider.is_tutorial && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-200"
                        data-testid="tutorial-badge"
                      >
                        Tutorial
                      </span>
                    )}
                  </div>
                  {provider.username && (
                    <p className="text-sm text-[#D4AF37] font-medium mt-0.5" data-testid="provider-username">
                      @{provider.username}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <ShareButton 
                    path={shareUrl}
                    title={`${provider.brand_name} on Zuriya`}
                    description={provider.bio}
                  />
                  {user?.user_id && provider.user_id && user.user_id !== provider.user_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 border-[#E6E4DD]"
                      onClick={() => {
                        if (user.role === 'provider') {
                          // Providers messaging other providers is not a
                          // product use case; send them to the messages page
                          // where the backend rules still apply.
                          navigate(`/messages?user=${provider.user_id}`);
                          return;
                        }
                        navigate(`/messages?user=${provider.user_id}`);
                      }}
                      data-testid="message-provider-btn"
                      title={`Message ${provider.brand_name}`}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Message
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-stone-500">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {provider.location}
                </span>
                {provider.rating > 0 && (
                  <span className="flex items-center gap-1 text-[#D4AF37]">
                    <Star className="h-4 w-4 fill-current" />
                    {provider.rating.toFixed(1)} ({provider.review_count} {provider.review_count === 1 ? 'review' : 'reviews'})
                  </span>
                )}
              </div>
              
              <p className="text-stone-600 mt-3 text-sm md:text-base">{provider.bio}</p>
              <SpecialtyBadges ids={provider.specialties} testId="profile-specialties" className="mt-3" />
              
              {/* Category & Service Type tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {provider.categories?.map((cat) => (
                  <span key={cat} className="px-3 py-1 bg-[#F9F6F0] text-[#D4AF37] rounded-full text-xs font-medium">
                    {CATEGORY_LABELS[cat] || cat}
                  </span>
                ))}
                {provider.service_types?.map((type) => {
                  const meta = SERVICE_TYPE_META[type];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  return (
                    <span key={type} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                      <Icon className="h-3 w-3" />
                      {meta.label}
                    </span>
                  );
                })}
              </div>

              {/* Booking Notice Period */}
              {noticeHours > 0 && (
                <div className="flex items-center gap-2 mt-4 p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="storefront-notice-period">
                  <Clock className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    Please book at least{' '}
                    <span className="font-semibold">
                      {noticeHours >= 168
                        ? '1 week'
                        : noticeHours >= 24
                          ? `${Math.round(noticeHours / 24)} day${noticeHours >= 48 ? 's' : ''}`
                          : `${noticeHours} hour${noticeHours > 1 ? 's' : ''}`}
                    </span>{' '}
                    in advance
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cancellation Policy */}
        {provider.cancellation_policy && (
          <div className="card-surface p-6 mb-6" data-testid="storefront-cancellation-policy">
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
              Cancellation Policy
            </h2>
            <p className="text-sm font-medium text-[#1A1A1A]">{getPolicy(provider.cancellation_policy.type).label}</p>
            <p className="text-sm text-stone-600 mt-1">{getPolicy(provider.cancellation_policy.type).detail}</p>
          </div>
        )}

        {/* Pre-Appointment Rules */}
        {provider.pre_appointment_rules?.length > 0 && (
          <div className="card-surface p-6 mb-6" data-testid="storefront-pre-appointment-rules">
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#D4AF37]" />
              Before Your Appointment
            </h2>
            <ul className="space-y-2">
              {provider.pre_appointment_rules.map((rule) => (
                <li key={rule} className="flex items-start gap-2 text-sm text-stone-600">
                  <span className="text-[#D4AF37] mt-0.5 shrink-0">&#10003;</span>
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Location Info */}
        {provider.service_types && provider.service_types.length > 0 && (
          <div className="card-surface p-6 mb-6" data-testid="provider-locations">
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#D4AF37]" />
              Where to Find Us
            </h2>
            <div className="space-y-2">
              {provider.service_types.includes('salon') && (
                <div className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-lg text-sm">
                  <Building2 className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Salon / Studio</span>
                    {provider.salon_address && <p className="text-stone-500">{formatAddress(provider.salon_address)}</p>}
                  </div>
                </div>
              )}
              {provider.service_types.includes('home') && (
                <div className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-lg text-sm">
                  <Home className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Home-based</span>
                    {(provider.home_address || provider.home_address_partial) && (
                      <p className="text-stone-500">
                        {formatAddress(provider.home_address) || formatAddress(provider.home_address_partial)}
                        {!provider.home_address && provider.home_address_partial && (
                          <span className="text-xs text-stone-400 block">Full address provided after booking</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {provider.service_types.includes('mobile') && (
                <div className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-lg text-sm">
                  <Car className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Mobile</span>
                    {provider.service_radius && (
                      <p className="text-stone-500">Travels up to {provider.service_radius} {provider.service_radius === 1 ? 'mile' : 'miles'}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <ProviderServices services={provider.services} />

        <ProviderPortfolio
          portfolio={provider.portfolio}
          transformations={provider.transformations}
        />

        <ProviderReviewsList
          reviews={reviews}
          rating={provider.rating}
          brandName={provider.brand_name}
        />
      </div>
    </div>
  );
}
